export default class PlaybackDb {
  constructor() {
    this.initializeDb();
    this.listenForTimeUpdates();
  }

  initializeDb() {
    this.initializePromise = new Promise((resolve, reject) => {
      const request = indexedDB.open('playback', 1);
      request.onerror = (event) => {
        console.error('Error initializing dtabase:', event);
        reject(event);
      };
      request.onsuccess = (event) => {
        this.db = event.target.result;
        resolve(event);
      };
      request.onupgradeneeded = (event) => {
        const db = event.target.result;
        db.createObjectStore('times', { keyPath: 'identifier' });
      };
    });
  }

  listenForTimeUpdates() {
    window.addEventListener("supercast-timeupdate", (event) => {
      const { itemType, itemId, currentTime, duration } = event.detail;
      this.logPlayback({ itemType, itemId, currentTime, duration });
    });
  }

  async getPlaybackData(itemType, itemId) {
    const [data] = await this.getPlaybackDataMany([{ itemType, itemId }]);
    return data;
  }

  // One read transaction for many items; results line up with items, undefined where nothing is stored.
  async getPlaybackDataMany(items) {
    await this.initializePromise;
    const store = this.db.transaction('times', 'readonly').objectStore('times');
    return Promise.all(items.map(({ itemType, itemId }) => new Promise((resolve, reject) => {
      const request = store.get(`${itemType}-${itemId}`);
      request.onerror = reject;
      request.onsuccess = (event) => resolve(event.target.result);
    })));
  }

  async logPlayback({ itemType, itemId, currentTime, duration }) {
    await this.initializePromise;
    const transaction = this.db.transaction('times', 'readwrite');
    const store = transaction.objectStore('times');
    store.put({
      identifier: `${itemType}-${itemId}`,
      currentTime,
      duration,
      lastPlayedAt: new Date()
    });
  }
}

// Number of seconds from the end within which we restart from 0 instead of
// resuming. Mirrors the web player rule in
// app/components/media/audio_player_controller.js (resumeTime).
const RESTART_THRESHOLD_SECONDS = 15;

// Listened to the end, by the same near-the-end rule that restarts an episode.
export function isFinished({ currentTime, duration }) {
  return !!duration && duration - currentTime < RESTART_THRESHOLD_SECONDS;
}

// Stable bridge for the native iOS/Android episode screens.
export function installNativePlaybackBridge(playbackDb) {
  window.supercastNativePlayback = {
    // Resolves to the position (seconds) the native player should seek to, or 0
    // to start from the beginning. Applies the same near-the-end restart rule as
    // the web player.
    async getResumePosition(itemType, itemId) {
      try {
        const data = await playbackDb.getPlaybackData(itemType, itemId);
        if (!data) return 0;
        const currentTime = data.currentTime || 0;
        const duration = data.duration || 0;
        if (isFinished({ currentTime, duration })) return 0;
        return currentTime;
      } catch (error) {
        console.error("supercastNativePlayback.getResumePosition failed", error);
        return 0;
      }
    },

    // Persists native playback progress to IndexedDB. Writes straight through
    // logPlayback (rather than dispatching supercast-timeupdate) so native
    // progress is not double-counted by the service-worker heatmap path.
    report({ itemType, itemId, currentTime, duration }) {
      playbackDb.logPlayback({ itemType, itemId, currentTime, duration });
    }
  };
}